import React, { useState, useMemo, useContext, useRef, useEffect } from "react"
import tw from "twin.macro"
import StoreItem from "./StoreItem"
import StoreMap from "./StoreMap"
import { GeneralContext } from "../../contexts/generalContext"
import "../ctaModule/styles/variables.css"
import "../ctaModule/styles/coloresOB.css"
import * as s from "../ctaModule/FormGeneral/styles.module.css"



const paisesTitle = {
  "es": "España",
  "pt": "Portugal"
}

const StoreList = ({ 
  stores = [], 
  paises = [],
  provincias = [], 
  localidades = [], 
  defaultPais = ""
}) => {
  const generalContext = useContext(GeneralContext)
  const { t } = generalContext

  // console.table(stores.map(store => ({
  //   title: store.title,
  //   localidad_pwid: store.localidad_pwid,
  //   provincia_pwid: store.provincia_pwid,
  //   pais_slug: store.pais_slug
  // })))
  // console.table(provincias.map(provincia => ({
  //   title: provincia.title,
  //   pwid: provincia.pwid
  // })))
  // console.table(localidades.map(localidad => ({
  //   title: localidad.title,
  //   pwid: localidad.pwid
  // })))

  const storesWithProvinciasAndLocalidades = useMemo(() => {
    return stores.map(store => ({
      ...store,
      provincia: provincias.find(provincia => provincia.pwid === store.provincia_pwid),
      localidad: localidades.find(localidad => localidad.pwid === store.localidad_pwid)
    }))
  }, [stores, provincias, provincias])

  const [searchTerm, setSearchTerm] = useState("")
  const [selectedPaisSlug, setSelectedPaisSlug] = useState(defaultPais)
  const [selectedProvinciaPWID, setSelectedProvinciaPWID] = useState("")
  const [selectedLocalidadPWID, setSelectedLocalidadPWID] = useState("")
  const [selectedStore, setSelectedStore] = useState(null)
  
  // Ref para el contenedor de la lista de tiendas
  const storeListRef = useRef(null)

  // Efecto para hacer scroll hasta la tienda seleccionada
  useEffect(() => {
    if (selectedStore && storeListRef.current) {
      // Buscar el elemento de la tienda seleccionada
      const storeElement = storeListRef.current.querySelector(`[data-store-id="${selectedStore.pwid}"]`)
      if (storeElement) {
        // Obtener las posiciones relativas al contenedor
        const containerRect = storeListRef.current.getBoundingClientRect()
        const elementRect = storeElement.getBoundingClientRect()
        
        // Calcular la posición del elemento dentro del contenedor
        const elementTop = elementRect.top - containerRect.top + storeListRef.current.scrollTop
        const containerHeight = storeListRef.current.clientHeight
        const elementHeight = elementRect.height
        
        // Calcular la posición para centrar el elemento
        const scrollToPosition = elementTop - (containerHeight / 2) + (elementHeight / 2)
        
        // Hacer scroll suave solo dentro del contenedor
        storeListRef.current.scrollTo({
          top: scrollToPosition,
          behavior: 'smooth'
        })
      }
    }
  }, [selectedStore])

  // Filtrar provincias basadas en el país seleccionado
  const filteredProvincias = useMemo(() => {
    if (!selectedPaisSlug) return provincias
    return provincias.filter(provincia => 
      provincia.pais_slug === selectedPaisSlug
    )
  }, [selectedPaisSlug, provincias])

  // Filtrar localidades basadas en la provincia seleccionada
  const filteredLocalidades = useMemo(() => {
    if (!selectedProvinciaPWID) return localidades
    const filtered = localidades.filter(localidad => {
      return localidad.provincia_pwid === selectedProvinciaPWID
    }
    )
    return filtered
  }, [selectedProvinciaPWID, localidades])

  // Filtrar tiendas basadas en los criterios de búsqueda
  const filteredStores = useMemo(() => {
    // onsole.log('Filtrando tiendas con criterios:', {
    //   searchTerm,
    //   selectedPaisSlug,
    //   selectedProvinciaPWID,
    //   selectedLocalidadPWID
    // })
    // onsole.log('Tiendas disponibles:', stores)
    
    const filtered = storesWithProvinciasAndLocalidades.filter(store => {
      const matchesSearch = !searchTerm || 
        store.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.direccion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.localidad?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.provincia?.title?.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesPais = !selectedPaisSlug || 
        store.pais_slug === selectedPaisSlug

      const matchesProvincia = !selectedProvinciaPWID || 
        store.provincia_pwid === selectedProvinciaPWID

      const matchesLocalidad = !selectedLocalidadPWID || 
        store.localidad_pwid === selectedLocalidadPWID

      const matches = matchesSearch && matchesPais && matchesProvincia && matchesLocalidad
      
      return matches
    })
    
    // Si la tienda seleccionada no está en los resultados filtrados, anularla
    if (selectedStore && !filtered.find(store => store.pwid === selectedStore.pwid)) {
      setSelectedStore(null)
    }

    // onsole.log('Tiendas filtradas:', filtered)
    return filtered
  }, [storesWithProvinciasAndLocalidades, searchTerm, selectedPaisSlug, selectedProvinciaPWID, selectedLocalidadPWID])

  const handlePaisChange = (e) => {
    setSelectedPaisSlug(e.target.value)
    setSelectedProvinciaPWID("") // Reset provincia when pais changes
    setSelectedLocalidadPWID("") // Reset localidad when pais changes
  }

  const handleProvinciaChange = (e) => {
    setSelectedProvinciaPWID(e.target.value)
    setSelectedLocalidadPWID("") // Reset localidad when provincia changes
  }

  const clearFilters = () => {
    setSearchTerm("")
    setSelectedPaisSlug(defaultPais) // Reset to default pais instead of empty
    setSelectedProvinciaPWID("")
    setSelectedLocalidadPWID("")
    setSelectedStore(null)
  }

  const handleStoreSelect = (store) => {
    setSelectedStore(store)
  }

  const handleStoreToggle = (store, isExpanded) => {
    if (isExpanded) {
      setSelectedStore(store)
    } else if (selectedStore?.pwid === store.pwid) {
      setSelectedStore(null)
    }
  }


  return (
    <div css={tw`w-full mb-8`}>


      {/* Filtros y búsqueda */}
      <div css={tw`mb-6`}  className={s.container}>
        <div css={tw`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4`}>
          {/* Búsqueda por texto */}
          <div css={tw`lg:col-span-2`}>
            <label css={tw`block text-sm font-medium text-gray-700 mb-1`}>
              Buscar tienda
            </label>
            <div css={tw`relative`}>
              <div className={`${s.inputContainer} variables-ob variables-cta`}>
                <input
                  type="text"
                  placeholder="Nombre, dirección, ciudad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '40px'
                  }}
                  // css={tw`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500`}
                />
              </div>
              <div css={tw`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none`}>
                <svg css={tw`h-5 w-5 text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Filtro por país */}
          <div>
            <label css={tw`block text-sm font-medium text-gray-700 mb-1`}>
              País
            </label>
            <select
              value={selectedPaisSlug}
              onChange={handlePaisChange}
              css={tw`w-full py-2 px-3 border border-gray-300 bg-white focus:ring-blue-500 focus:border-blue-500`}
            >
              <option value="">Todos los países</option>
              {paises.map((pais, idx) => (
                <option key={`${idx}-${pais.slug}`} value={pais.slug}>
                  {paisesTitle[pais.slug] || pais.title}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro por provincia */}
          <div>
            <label css={tw`block text-sm font-medium text-gray-700 mb-1`}>
              Provincia
            </label>
            <select
              value={selectedProvinciaPWID}
              onChange={handleProvinciaChange}
              disabled={!selectedPaisSlug}
              css={[
                tw`w-full py-2 px-3 border border-gray-300 focus:ring-blue-500 focus:border-blue-500`,
                selectedPaisSlug ? tw`bg-white` : tw`bg-transparent cursor-not-allowed`
              ]}
            >
              <option value="">Todas las provincias</option>
              {filteredProvincias.map((provincia, idx) => (
                <option key={`${idx}-${provincia.pwid}`} value={provincia.pwid}>
                  {provincia.title}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro por localidad */}
          <div>
            <label css={tw`block text-sm font-medium text-gray-700 mb-1`}>
              Localidad
            </label>
            <select
              value={selectedLocalidadPWID}
              onChange={(e) => setSelectedLocalidadPWID(e.target.value)}
              disabled={!selectedProvinciaPWID}
              css={[
                tw`w-full py-2 px-3 border border-gray-300 focus:ring-blue-500 focus:border-blue-500`,
                selectedProvinciaPWID ? tw`bg-white` : tw`bg-transparent cursor-not-allowed`
              ]}
            >
              <option value="">Todas las localidades</option>
              {filteredLocalidades.map((localidad, idx) => (
                <option key={`${idx}-${localidad.pwid}`} value={localidad.pwid}>
                  {localidad.title}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Botón para limpiar filtros */}
        {/* {(searchTerm || selectedPaisSlug !== defaultPais || selectedProvinciaPWID || selectedLocalidadPWID) && (
          <div css={tw`mt-4 flex justify-end`}>
            <button
              onClick={clearFilters}
              css={tw`inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200`}
            >
              <svg css={tw`w-4 h-4 mr-1`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Limpiar filtros
            </button>
          </div>
        )} */}
      </div>


      {/* Contador de resultados */}
      <div css={tw`w-full pb-2`}>
        <p css={tw`text-sm text-gray-600`}>
          {filteredStores.length === 0 
            ? "No se encontraron tiendas con los criterios seleccionados"
            : `${filteredStores.length} ${filteredStores.length === 1 ? 'tienda encontrada' : 'tiendas encontradas'}`
          }
        </p>
      </div>


      {/* Lista de tiendas y mapa */}
      <div
        tw="md:grid grid-cols-3 gap-0"
        style={{
          height: 'calc(100vh - 260px)'
        }}
      >
        
        {/* Lista de tiendas */}
        <div css={tw`h-full overflow-y-auto bg-white`} ref={storeListRef}>
          <div css={tw``}>
            {filteredStores.length === 0 ? (
              <div css={tw`text-center py-12`}>
                <svg css={tw`mx-auto h-12 w-12 text-gray-400 mb-4`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h3 css={tw`text-lg font-medium text-gray-900 mb-2`}>
                  No hay tiendas disponibles
                </h3>
                <p css={tw`text-gray-500 mb-4`}>
                  Intenta ajustar tus criterios de búsqueda o filtros.
                </p>
                {(searchTerm || selectedPaisSlug !== defaultPais || selectedProvinciaPWID || selectedLocalidadPWID) && (
                  <button
                    onClick={clearFilters}
                    css={tw`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200`}
                  >
                    Ver todas las tiendas
                  </button>
                )}
              </div>
            ) : (
              filteredStores.map((store, idx) => (
                <div key={`${idx}-${store.pwid}`} data-store-id={store.pwid}>
                  <StoreItem 
                    number={idx + 1}
                    store={store}
                    onToggle={handleStoreToggle}
                    isSelected={selectedStore?.pwid === store.pwid}
                    t={t}
                  />
                </div>
              ))
            )}
          </div>
        </div>


        {/* Mapa */}
        <div css={tw`col-span-2`}>
            <StoreMap
              stores={filteredStores}
              selectedStore={selectedStore}
              onStoreSelect={handleStoreSelect}
            />
        </div>
      </div>

      
    </div>
  )
}

export default StoreList 